import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { CheckCircle2, CircleAlert, KeyRound, ShieldCheck } from "lucide-react";
import {
  DEFAULT_WEEKDAY_HOURS,
  LOCK_AFTER_WEEKS,
  MAX_HOURS_PER_DAY,
  MAX_HOURS_PER_WEEK,
  MAX_NOTE_LENGTH,
  NOTE_REQUIRED_ABOVE_HOURS,
} from "@contractorhub/shared";
import { authConfigQuery } from "../../../lib/queries";
import { Card, PageHeader } from "../../../components/ui";

export const Route = createFileRoute("/_authed/_admin/settings")({
  loader: ({ context }) => context.queryClient.ensureQueryData(authConfigQuery),
  component: SettingsPage,
});

function SettingsPage() {
  const { data } = useSuspenseQuery(authConfigQuery);

  return (
    <div>
      <PageHeader
        title="Settings"
        description="How sign-in and timesheet rules are configured for this workspace. These values come from the deployment — nothing here is editable in the app."
      />

      {/* No `max-w-4xl` any more: this was the one page that constrained itself,
          so navigating to it made the content visibly jump to half-width. The
          shell owns the page width now. */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound className="w-4 h-4 text-accent" />
            <h2 className="text-base font-semibold">Sign-in</h2>
          </div>

          <Row
            title="Microsoft Entra External ID"
            hint="Everyone signs in through Entra; passwords and reset are Entra's."
            state={data.configured ? "on" : "warn"}
            label={data.configured ? "Configured" : "Not configured"}
            last
          />

          {!data.configured && (
            <p className="text-xs text-subtle mt-3">
              Set the <code className="font-mono">ENTRA_*</code> application settings so the
              SPA can configure MSAL and users can sign in.
            </p>
          )}
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="w-4 h-4 text-accent" />
            <h2 className="text-base font-semibold">Account protection</h2>
          </div>

          <Row
            title="Password minimum"
            hint="For any password a person chooses."
            value="12 characters"
          />
          <Row
            title="Account lockout"
            hint="Consecutive failed sign-ins."
            value="5 → 15 min"
          />
          <Row
            title="Session lifetime"
            hint="Absolute, and idle timeout."
            value="7 days / 24 h"
          />
          <Row
            title="Deactivation"
            hint="Revokes every live session at once."
            value="Immediate"
            last
          />
        </Card>

        <Card className="p-5 lg:col-span-2">
          <h2 className="text-base font-semibold mb-4">Timesheet rules</h2>
          <div className="grid gap-x-8 sm:grid-cols-2">
            <Row
              title="Hours per day"
              hint="In whole hours."
              value={`${MAX_HOURS_PER_DAY} h max`}
            />
            <Row
              title="Hours per week"
              hint="Above 40 h asks for confirmation."
              value={`${MAX_HOURS_PER_WEEK} h max`}
            />
            {/* TIME-4. The bound is per week, not per day: the current week is
                fillable to its last day, so a contractor can still submit on
                Friday for the week they are in. */}
            <Row
              title="Future weeks"
              hint="A week opens on its own Monday."
              value="Not fillable"
            />
            <Row
              title="Old weeks"
              hint="Unless an admin returns the timesheet."
              value={`Locked after ${LOCK_AFTER_WEEKS} weeks`}
            />
            <Row
              title="Notes"
              hint={`Required to submit a day over ${NOTE_REQUIRED_ABOVE_HOURS} h, or any weekend hours.`}
              value={`${MAX_NOTE_LENGTH} characters`}
              last
            />
            <Row
              title="New weekdays"
              hint="A suggestion only — nothing is filed until the contractor saves."
              value={`Pre-filled at ${DEFAULT_WEEKDAY_HOURS} h`}
              last
            />
          </div>
          <p className="text-xs text-subtle mt-4">
            These are enforced by the API and mirrored in the editor. Changing them means
            changing <code className="font-mono">packages/shared</code>, so they stay in
            step with the database constraints.
          </p>
        </Card>
      </div>
    </div>
  );
}

function Row({
  title,
  hint,
  state,
  label,
  value,
  last,
}: {
  title: string;
  hint: string;
  state?: "on" | "warn";
  label?: string;
  value?: string;
  last?: boolean;
}) {
  const Icon = state === "on" ? CheckCircle2 : CircleAlert;
  const tone = state === "on" ? "text-success-on-soft" : "text-warning-on-soft";

  return (
    <div
      className={`flex items-center justify-between gap-4 py-3 ${last ? "" : "border-b border-line"}`}
    >
      <div className="min-w-0">
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs text-subtle">{hint}</div>
      </div>
      {state ? (
        <span className={`inline-flex items-center gap-1.5 text-sm shrink-0 ${tone}`}>
          <Icon className="w-4 h-4" /> {label}
        </span>
      ) : (
        <span className="text-sm text-muted shrink-0 whitespace-nowrap">{value}</span>
      )}
    </div>
  );
}
